import { RiskDashboardDto } from "@/features/orders-analytics/types/risk-analytics.types";
import { formatters } from "@/features/orders-analytics/utils/formatters";

export type SettlementRowVM = {
    rail: string;
    settledOrders: number;
    settledVolume: string;
    inFlightOrders: number;
    inFlightVolume: string;
};

export type ProviderSettlementRowVM = {
    provider: string;
    settledOrders: number;
    settledVolume: string;
    inFlightOrders: number;
    inFlightVolume: string;
};

export type ValidationBlockTypeRowVM = { type: string; count: number };

export type ComplianceVM = {
    underReview: number;
    validationBlocked: number;
    manualOps: number;
    kycCoveragePercent: number | null;
    validationBlockTypes: ValidationBlockTypeRowVM[];
};

export type ProblemOrdersVM = {
    blocked: number;
    stuck: number;
    negativeProfit: number;
    blockedVolume: string;
};

export type RiskScoreInputVM = { label: string; weight: number; normalized: number };

export type RiskScoreVM = { value: number; inputs: RiskScoreInputVM[] };

export type RiskVM = {
    settlementByRail: SettlementRowVM[];
    settlementByProvider: ProviderSettlementRowVM[];
    compliance: ComplianceVM | null;
    problemOrders: ProblemOrdersVM | null;
    score: RiskScoreVM | null;
    treasuryLiquidity: null;
    amlFlags: null;
};

const EMPTY_VM: RiskVM = {
    settlementByRail: [],
    settlementByProvider: [],
    compliance: null,
    problemOrders: null,
    score: null,
    treasuryLiquidity: null,
    amlFlags: null,
};

const derivedKycCoveragePercent = (kycCoveredOrders: number, totalOrders: number): number | null =>
    totalOrders > 0 ? (kycCoveredOrders / totalOrders) * 100 : null;

const fromData = (dto: RiskDashboardDto | null): RiskVM => {
    if (dto === null) {
        return EMPTY_VM;
    }

    return {
        settlementByRail: dto.settlement.byRail.map((row) => ({
            rail: row.rail,
            settledOrders: row.settledOrders,
            settledVolume: formatters.formatCurrency(row.settledVolume),
            inFlightOrders: row.inFlightOrders,
            inFlightVolume: formatters.formatCurrency(row.inFlightVolume),
        })),
        settlementByProvider: dto.settlement.byProvider.map((row) => ({
            provider: row.provider,
            settledOrders: row.settledOrders,
            settledVolume: formatters.formatCurrency(row.settledVolume),
            inFlightOrders: row.inFlightOrders,
            inFlightVolume: formatters.formatCurrency(row.inFlightVolume),
        })),
        compliance: {
            underReview: dto.compliance.underReview,
            validationBlocked: dto.compliance.validationBlocked,
            manualOps: dto.compliance.manualOps,
            kycCoveragePercent: derivedKycCoveragePercent(
                dto.compliance.kycCoveredOrders,
                dto.compliance.totalOrders
            ),
            validationBlockTypes: dto.compliance.validationBlockTypes.map((row) => ({
                type: row.type,
                count: row.count,
            })),
        },
        problemOrders: {
            blocked: dto.problemOrders.blocked,
            stuck: dto.problemOrders.stuck,
            negativeProfit: dto.problemOrders.negativeProfit,
            blockedVolume: formatters.formatCurrency(dto.problemOrders.blockedVolume),
        },
        score: {
            value: dto.score.value,
            inputs: dto.score.inputs.map((input) => ({
                label: input.label,
                weight: input.weight,
                normalized: input.normalized,
            })),
        },
        treasuryLiquidity: null,
        amlFlags: null,
    };
};

export const riskViewModel = { fromData };
